import { api, getAccessToken } from '@/api/client'
import type {
  AssetDetail, AssetImage, AssetSummary, CreateUserPayload, CurrentUser, DashboardSummary, HistoryEntry,
  Lookups, ScanResult, UserItem,
} from '@/types'

export const authApi = {
  login: (email: string, password: string) =>
    api.post<{ token: { access_token: string; expires_in: number }; user: CurrentUser }>(
      '/auth/login', { email, password },
    ),
  register: (fullName: string, email: string, password: string) =>
    api.post<{ token: { access_token: string; expires_in: number }; user: CurrentUser }>(
      '/auth/register', { full_name: fullName, email, password },
    ),
  forgotPassword: (email: string) =>
    api.post<{ message: string; reset_token: string | null }>('/auth/forgot-password', { email }),
  resetPassword: (token: string, newPassword: string) =>
    api.post<{ token: { access_token: string; expires_in: number }; user: CurrentUser }>(
      '/auth/reset-password', { token, new_password: newPassword },
    ),
  logout: () => api.post<{ signed_out: boolean }>('/auth/logout'),
  me: () => api.get<CurrentUser>('/auth/me'),
}

export const assetsApi = {
  scan: (qrValue: string, source: 'CAMERA' | 'MANUAL' = 'CAMERA') =>
    api.post<ScanResult>('/assets/scan', { qr_value: qrValue, source }),

  create: (payload: Record<string, unknown>, scanToken: string) =>
    api.post<AssetDetail>('/assets', payload, { 'X-Scan-Token': scanToken }),

  list: (params: Record<string, string | number | boolean | undefined>) => {
    const query = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') query.set(key, String(value))
    })
    return api.get<{ items: AssetSummary[]; total: number; limit: number; offset: number }>(
      `/assets?${query.toString()}`,
    )
  },

  exportData: (params: Record<string, string | number | boolean | undefined> = {}) => {
    const query = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') query.set(key, String(value))
    })
    return api.get<{ items: Record<string, any>[]; total: number }>(
      `/assets/export-data?${query.toString()}`,
    )
  },

  downloadPhotosZip: async (params: Record<string, string | number | boolean | undefined> = {}) => {
    const query = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') query.set(key, String(value))
    })
    const token = getAccessToken()
    const response = await fetch(`/api/v1/assets/export-photos-zip?${query.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!response.ok) {
      throw new Error(`Failed to download photos archive (status: ${response.status})`)
    }
    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `TRC_Asset_Photos_${new Date().toISOString().slice(0, 10)}.zip`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  },

  detail: (id: string) => api.get<AssetDetail>(`/assets/${id}`),
  byCode: (code: string) => api.get<AssetDetail>(`/assets/by-code/${encodeURIComponent(code)}`),
  update: (id: string, payload: Record<string, unknown>) =>
    api.patch<AssetDetail>(`/assets/${id}`, payload),
  delete: (id: string) => api.delete<{ deleted: boolean; asset_id: string }>(`/assets/${id}`),
  verify: (id: string) => api.post<AssetDetail>(`/assets/${id}/verify`),
  history: (id: string) => api.get<{ items: HistoryEntry[] }>(`/assets/${id}/history`),
}

export const imagesApi = {
  presign: (assetId: string, payload: {
    filename?: string; mime_type: string; file_size: number; image_type: string
    checksum_sha256?: string
  }) =>
    api.post<{
      image_id: string; upload_url: string; method: string
      headers: Record<string, string>; expires_in: number
    }>(`/assets/${assetId}/images/presign`, payload),

  complete: (assetId: string, imageId: string) =>
    api.post<{ id: string; is_primary: boolean; upload_state: string }>(
      `/assets/${assetId}/images/complete`, { image_id: imageId },
    ),

  list: (assetId: string) => api.get<{ items: AssetImage[] }>(`/assets/${assetId}/images`),
  setPrimary: (assetId: string, imageId: string) =>
    api.patch(`/assets/${assetId}/images/${imageId}`, { is_primary: true }),
  remove: (assetId: string, imageId: string) =>
    api.delete(`/assets/${assetId}/images/${imageId}`),
}

export const lookupsApi = { all: () => api.get<Lookups>('/lookups') }
export const dashboardApi = { summary: () => api.get<DashboardSummary>('/dashboard/summary') }

export const usersApi = {
  list: (query?: string) => {
    const q = query ? `?query=${encodeURIComponent(query)}` : ''
    return api.get<{ items: UserItem[]; total: number }>(`/users${q}`)
  },
  create: (payload: CreateUserPayload) =>
    api.post<UserItem>('/users', payload),
}
